import { formatAsMonthAndYear } from "../helpers/date-formatting-util.js";
import chartBuilderBase from "./chartBuilderBase.js";
import chartDisplaySettings from "./chartDisplaySettings.js";
import periodSelection from "./periodSelection.js";
import responsive from "../responsive.js";

/**
 * Builds default date xAxis options http://api.highcharts.com/highcharts#xAxis
 *
 * @param {object} spec - Parameter object
 * @param {object} my - protected fields holder
 * @return {{}}
 */
function xDateAxisBuilder(spec, my) {
	spec ||= {};
	my ||= {};

	const builderSpec = Object.create(spec);

	builderSpec.options = chartBuilderBase.mergeOptions(
		builderSpec.options || {},
		{
			lineWidth: 0,
			minorGridLineWidth: 0,
			minorTickLength: 0,
			tickLength: 0,
			gridLineWidth: 0,
			type: "datetime",
			labels: {
				formatter: function () {
					return formatDate(this);
				},
				rotation: responsive.isMobileBreakpoint() ? -90 : 0,
				style: {
					paddingLeft: responsive.isMobileBreakpoint()
						? "40px"
						: "0px",
					paddingTop: responsive.isMobileBreakpoint() ? "5px" : "0px",
					fontFamily: chartDisplaySettings.font,
					fontSize: chartDisplaySettings.axisFontSize,
					color: chartDisplaySettings.axisLabelColor,
				},
				y: 20,
			},
		},
	);

	let plotBandMouseClick = function () {};
	let selectPeriodClickHandler = function () {};

	let preventScroll = true; // Only used in plotBandMouseClick
	const plotBandEvents = {
		mouseover: spec.mouseOver || function () {},
		mouseout: spec.mouseOut || function () {},
		touchmove: function () {
			preventScroll = true;
		},
		touchend: function () {
			if (preventScroll) {
				preventScroll = false;
				return;
			}
			plotBandMouseClick(this);
		},
	};

	my.periodSelection = periodSelection();

	let periodType = "Day";
	let dateFormats = {};

	const that = chartBuilderBase(builderSpec, my);

	my.myself = that;
	my.periods = [];

	/**
	 * Overrides the base builder method. See `buildDateTimeXAxis`.
	 *
	 * @return {*}
	 */
	that.build = function () {
		return my.buildDateTimeXAxis();
	};

	/**
	 * Separates the chart into periods of selected type. Highlights hovered period
	 * with hoverColor.
	 *
	 * @param {object} options - Object parameter
	 * @param {object[]} options.periods - List of periods to set
	 * @param {period} options.selectedPeriod - Period to select
	 * @return {chartBuilderBase} receiver for chaining
	 */
	that.setPeriods = function (options) {
		my.periods = options.periods.slice();
		periodType = my.periods[0].getPeriodType();

		return that
			.setSelectedPeriod(options.selectedPeriod)
			.setPeriodPlotBands(my.periods)
			.setPeriodPlotLines(my.periods)
			.setPlotBandEvents({
				mouseover: function () {
					const selectedPeriod =
						my.periodSelection.getSelectedPeriod();
					const index = this.options.index;
					const data = this.axis.series[0].data;
					if (data.length && this.options.period) {
						const value = data[index].y;

						if (value === null) {
							return;
						}

						if (
							!selectedPeriod ||
							!selectedPeriod.includes(this.options.period)
						) {
							this.svgElem.attr(
								"fill",
								chartDisplaySettings.hoverColor,
							);
						}
					}
				},
				mouseout: function () {
					// resets the color from hovering
					this.svgElem.attr("fill", this.options.color);
				},
			});
	};

	that.setTickInterval = function (value) {
		return that.set("tickInterval", value);
	};

	that.setYearTickInterval = function () {
		return that.setTickInterval(365.25 * 24 * 60 * 60 * 1000);
	};

	that.setHalfYearTickInterval = function () {
		return that.setTickInterval(365.25 * 12 * 60 * 60 * 1000);
	};

	that.setQuarterTickInterval = function () {
		return that.setTickInterval(365.25 * 6 * 60 * 60 * 1000);
	};

	that.setBimesterTickInterval = function () {
		return that.setTickInterval(2 * 4 * 7 * 24 * 3600 * 1000);
	};

	that.setMonthTickInterval = function () {
		return that.setTickInterval(4 * 7 * 24 * 3600 * 1000);
	};

	that.setWeekTickInterval = function () {
		return that.setTickInterval(7 * 24 * 3600 * 1000);
	};

	that.setDayTickInterval = function () {
		return that.setTickInterval(24 * 3600 * 1000);
	};

	/**
	 *
	 * Automatically compute the tickInterval function to use depending on the
	 * current period type. A specific set of interval functions can be passed
	 * as argument.
	 *
	 * @param {*} [tickIntervals] - set of interval functions. It will be merged with the default
	 *                            so one can only specify a subset of intervals.
	 * @return {*}
	 */
	that.setTickIntervalFromPeriodType = function (tickIntervals) {
		const defaultIntervals = {
			Day: that.setDayTickInterval,
			Week: that.setWeekTickInterval,
			Month: that.setMonthTickInterval,
			Bimester: that.setBimesterTickInterval,
			Quarter: that.setQuarterTickInterval,
			HalfYear: that.setHalfYearTickInterval,
			Year: that.setYearTickInterval,
		};

		const intervals = { ...defaultIntervals, ...tickIntervals };
		const tickIntervalSetter = intervals[periodType];

		if (!tickIntervalSetter) {
			throw "Unknown period type";
		}

		return tickIntervalSetter();
	};

	that.setSelectablePeriods = function (options) {
		if (options.periodSelection) {
			my.periodSelection = options.periodSelection;
		}
		let currentPeriod = my.periodSelection.getSelectedPeriod();

		selectPeriodClickHandler = function (clickedPlotBand) {
			// Only plot bands
			if (!clickedPlotBand.options.period) {
				return;
			}

			const index = clickedPlotBand.options.index;
			const value = clickedPlotBand.axis.series[0].data[index].y;
			if (value === null) {
				return;
			}

			// Set selected period
			currentPeriod = clickedPlotBand.options.period;

			// Update chart
			my.periodSelection.selectPlotBand(
				clickedPlotBand.axis.plotLinesAndBands,
				currentPeriod,
			);

			if (options && options.plotBandClicked) {
				options.plotBandClicked(clickedPlotBand);
			}
		};

		return my.myself;
	};

	/**
	 * Highlights period
	 *
	 * @param {Period} period - Period to select
	 * @return {chartBuilderBase} receiver for chaining
	 */
	that.setSelectedPeriod = function (period) {
		my.periodSelection.setSelectedPeriod(period);
		return my.myself;
	};

	/**
	 * Registers events for all plot bands
	 *
	 * @param {{}} events - mouseover, mouseout, touchmove, touchend and click
	 */
	that.setPlotBandEvents = function (events) {
		plotBandEvents.mouseover = events.mouseover || plotBandEvents.mouseover;
		plotBandEvents.mouseout = events.mouseout || plotBandEvents.mouseout;
		plotBandEvents.touchmove = events.touchmove || plotBandEvents.touchmove;
		plotBandEvents.touchend = events.touchend || plotBandEvents.touchend;

		plotBandMouseClick = events.click || plotBandMouseClick;

		return my.myself;
	};

	/**
	 * Adds plot line
	 *
	 * @param {*|chartBuilderBase} plotLine - Plotline to set
	 * @return {chartBuilderBase} receiver for chaining
	 */
	that.setPlotLine = function (plotLine) {
		return that.setItem("plotLine", plotLine);
	};

	/**
	 * Adds two plot lines for the given period, one at its
	 * beginning and one at its end
	 *
	 * @param {period} period - Period for the plot line
	 * @return {chartBuilderBase} receiver for chaining
	 */
	that.setPeriodPlotLine = function (period) {
		that.setPlotBand({
			color: chartDisplaySettings.chartBackgroundColor,
			width: 1,
			value: period.getStart().valueOf(),
			zIndex: 0,
			dashStyle: "dot",
		});

		that.setPlotBand({
			color: chartDisplaySettings.chartBackgroundColor,
			width: 1,
			value: period.getEnd().valueOf(),
			zIndex: 0,
			dashStyle: "dot",
		});

		return my.myself;
	};

	/**
	 * Adds plot lines for periods between two dates
	 *
	 * @return {chartBuilderBase} receiver for chaining
	 */
	that.setPeriodPlotLines = function (periods) {
		periods.forEach(that.setPeriodPlotLine);

		return my.myself;
	};

	/**
	 * Adds a plot band
	 *
	 * @param {object} plotBand - Plot band to set
	 * @return {chartBuilderBase}
	 */
	that.setPlotBand = function (plotBand) {
		return that.setItem("plotBands", plotBand);
	};

	/**
	 * Adds a plot band for period starting at `period.start`.
	 *
	 * @param {period} period - Period to which correspond the plot band
	 * @param {number} index - Index of the plot band
	 * @return {chartBuilderBase} receiver
	 */
	that.setPeriodPlotBand = function (period, index) {
		const options = {
			period,
			index,
			cursor: function (plotband) {
				const datas = plotband.axis.series.map(
					(series) => series.options.data,
				);
				const points = datas.map((data) =>
					data.find((point) =>
						plotband.options.period.includes(new Date(point.x)),
					),
				);

				const haveData = points.some(
					(point) => point && point.y !== null,
				);

				if (haveData) {
					return "pointer";
				}
				return "auto";
			},
			color: chartDisplaySettings.backgroundColor,
			from: period.getStart().valueOf(),
			to: period.getEnd().valueOf(),
		};

		return that.setPlotBand(options);
	};

	/**
	 * Adds plot bands for periods
	 *
	 * @return {chartBuilderBase} receiver
	 */
	that.setPeriodPlotBands = function (periods) {
		periods.forEach(that.setPeriodPlotBand);

		return my.myself;
	};

	that.setDateFormat = function (options) {
		dateFormats = Object.create(options);

		return my.myself;
	};

	my.buildDateTimeXAxis = function () {
		const options = my.buildOptions();
		const selectedPeriod = my.periodSelection.getSelectedPeriod();

		// Add click handler but only if not mobile since it will cause problems with
		// scrolling (probably because it captures all mouse events)
		if (!responsive.isTouchDevice()) {
			plotBandEvents.click = function () {
				plotBandMouseClick.apply(this, arguments);
				selectPeriodClickHandler(this);
			};
		}

		// Plot Band Events
		if (options.plotBands) {
			options.plotBands.forEach((plotBand) => {
				plotBand.events = plotBandEvents;
			});
		}

		// Highlight current month
		if (options.plotBands && selectedPeriod) {
			options.plotBands.forEach((plotBand) => {
				if (
					!plotBand.period ||
					!selectedPeriod.includes(plotBand.period)
				) {
					return;
				}
				plotBand.color = chartDisplaySettings.highlightColor;
			});
		}

		if (my.periods.length) {
			options.min ||= my.periods[0].getStart().valueOf();

			options.max ||= my.periods.at(-1).getEnd().valueOf();
		}

		return options;
	};

	/**
	 * Format a date on a date format specified by the current period type. It will default to `monthAndYear`
	 * (e.g. Jan 2014) if no period specific date formatting has been specified.
	 *
	 * @param {object} options - Object parameter
	 * @param {number} options.value - Value representing a date
	 * @param {boolean} options.isFirst - True if the date is the first day of a month
	 * @param {boolean} options.isLast - True if the date it the last day of a month
	 * @return {string}
	 */
	function formatDate(options) {
		const formatter =
			dateFormats[periodType] ||
			function (aDate) {
				return formatAsMonthAndYear(aDate);
			};
		return formatter(new Date(options.value), options);
	}

	return that;
}

export default xDateAxisBuilder;
